import { memo, useCallback, useMemo, type ReactNode } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import Box from "@mui/material/Box";

import type {
  TableAction,
  TableColumn,
} from "src/components/GenericTable/types";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import type { MessageTemplate } from "../types";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import {
  buildTemplatePreviewPlain,
  formatTemplateDisplayName,
  getFluxmedDefaultTemplateCategory,
  getFluxmedDefaultCategoryLabel,
  isMessageTemplateApproved,
  padraoFluxmedChipSx,
} from "../utils/messageTemplateUiUtils";
import {
  getFluxmedDefaultCategoryChipColor,
  getFluxmedDefaultCategoryIcon,
} from "../utils/fluxmedDefaultTemplateCategoryIcons";
import {
  MessageTemplatesTableCreatedCell,
  MessageTemplatesTablePreviewCell,
  MessageTemplatesTableStatusCell,
  MessageTemplatesTableTemplateCell,
} from "./MessageTemplatesTableViewCells";

export interface MessageTemplatesTableViewProps {
  templates: MessageTemplate[];
  organizationId?: string;
  currentPage: number;
  onChangePage: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditTemplate: (template: MessageTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  onSendTemplate: (template: MessageTemplate) => void;
  allowSend?: boolean;
  allowDelete?: boolean;
  allowEdit?: boolean;
  isDefaultTemplateRow?: (template: MessageTemplate) => boolean;
  hasActiveMetaEndpoint?: boolean;
}

type MessageTemplateTableRow = MessageTemplate & {
  templateCell: ReactNode;
  statusCell: ReactNode;
  previewCell: ReactNode;
  createdCell: ReactNode;
};

const MessageTemplatesTableView = memo(
  ({
    templates,
    organizationId,
    currentPage,
    onChangePage,
    rowsPerPage,
    onRowsPerPageChange,
    onEditTemplate,
    onDeleteTemplate,
    onSendTemplate,
    allowSend = true,
    allowDelete = true,
    allowEdit = true,
    isDefaultTemplateRow,
    hasActiveMetaEndpoint,
  }: MessageTemplatesTableViewProps) => {
    const tableColumns: TableColumn[] = useMemo(
      () => [
        { column: "Modelo", rowName: "templateCell", align: "left", width: "28%" },
        { column: "Status", rowName: "statusCell", align: "left", width: "120px" },
        { column: "Prévia", rowName: "previewCell", align: "left" },
        {
          column: "Criado em",
          rowName: "createdCell",
          align: "left",
          width: "128px",
        },
      ],
      [],
    );

    const tableRows: MessageTemplateTableRow[] = useMemo(
      () =>
        templates.map((template) => {
          const previewPlain = buildTemplatePreviewPlain(template);
          const displayName = formatTemplateDisplayName(template.name, {
            organizationPrefix: organizationId,
            hasActiveMetaEndpoint,
          });

          const showDefaultBadge = isDefaultTemplateRow?.(template) ?? false;
          const templateCategory = showDefaultBadge
            ? getFluxmedDefaultTemplateCategory(
                template.name,
                organizationId,
                hasActiveMetaEndpoint,
              )
            : null;
          const CategoryIcon = templateCategory
            ? getFluxmedDefaultCategoryIcon(templateCategory)
            : null;

          return {
            ...template,
            templateCell: (
              <Stack spacing={0.5}>
                <MessageTemplatesTableTemplateCell displayName={displayName} />
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {showDefaultBadge && templateCategory && CategoryIcon ? (
                    <Chip
                      icon={<CategoryIcon sx={{ fontSize: "14px !important" }} />}
                      label={getFluxmedDefaultCategoryLabel(templateCategory)}
                      size="small"
                      color={getFluxmedDefaultCategoryChipColor(templateCategory)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  ) : null}
                  {showDefaultBadge ? (
                    <Chip
                      label="Padrão Fluxmed"
                      size="small"
                      variant="outlined"
                      sx={padraoFluxmedChipSx}
                    />
                  ) : null}
                </Stack>
              </Stack>
            ),
            statusCell: <MessageTemplatesTableStatusCell status={template.status} />,
            previewCell: (
              <MessageTemplatesTablePreviewCell previewPlain={previewPlain} />
            ),
            createdCell: (
              <MessageTemplatesTableCreatedCell createdAtIso={template.createdAtIso} />
            ),
          };
        }),
      [hasActiveMetaEndpoint, isDefaultTemplateRow, organizationId, templates],
    );

    const tableStyles = useMemo(
      () => ({
        container: {
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          maxWidth: { xs: "80vw", md: "90vw", lg: "100%" },
        },
        cell: {
          verticalAlign: "top",
          py: 1.25,
        },
        header: {
          fontWeight: 700,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "text.secondary",
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 1.25,
        },
      }),
      [],
    );

    const handleTablePageChange = useCallback(
      (_event: unknown, newPage: number) => {
        onChangePage(newPage + 1);
      },
      [onChangePage],
    );

    const tableActions: TableAction<MessageTemplateTableRow>[] = useMemo(() => {
      const actions: TableAction<MessageTemplateTableRow>[] = [];

      if (allowSend) {
        actions.push({
          icon: <WhatsAppIcon fontSize="small" sx={{ color: "success.main" }} />,
          tooltip: "Enviar mensagem",
          onClick: (row) => onSendTemplate(row),
          isDisabled: (row) => !isMessageTemplateApproved(row.status),
        });
      }

      if (allowEdit) {
        actions.push({
          icon: <EditOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />,
          tooltip: "Editar modelo",
          onClick: (row) => onEditTemplate(row),
        });
      }

      if (allowDelete) {
        actions.push({
          icon: <DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} />,
          tooltip: "Excluir modelo",
          onClick: (row) => onDeleteTemplate(row.id),
        });
      }

      return actions;
    }, [allowDelete, allowEdit, allowSend, onDeleteTemplate, onEditTemplate, onSendTemplate]);

    return (
      <Box sx={{ mt: 1.5 }}>
        <GenericTable<MessageTemplateTableRow>
          tableColumns={tableColumns}
          tableRows={tableRows}
          characterLimit={200}
          actions={tableActions}
          page={currentPage - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleTablePageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          styles={tableStyles}
        />
      </Box>
    );
  },
);

MessageTemplatesTableView.displayName = "MessageTemplatesTableView";

export default MessageTemplatesTableView;
